/**
 * Application color palette definitions used across styles and components.
 */
export const palette = {
    background: '#fff5ec',
    surface: '#fffdf8',
    panel: '#fff7f1',
    border: '#e7d3c4',
    text: '#2c2c2c',
    muted: '#6d6d6d',
    subtle: '#9c8171',
    accent: '#f28c5c',
    accentSoft: '#ffe5d3',
    accentDark: '#c16b3a',
    overlay: 'rgba(0,0,0,0.18)',
    overlaySoft: 'rgba(0,0,0,0.08)',
    inputBg: '#fff3e9',
    inputBorder: '#e3c7b6',
    inputText: '#2c2c2c',
    shadow: '0 10px 30px rgba(0,0,0,0.08)',
};

/**
 * OST canvas dot-grid appearance (see components/canvas/GridLayer.tsx, which defaults to these
 * values, and Canvas.tsx, which renders it) — the reference "house style" for a dot grid in this
 * app. Declared here, once, so anything else wanting the identical look (the Plan page's
 * Timeline canvas below) reads from these instead of re-declaring its own copy that could drift.
 */
export const ostGridDotSpacing = 15;
export const ostGridDotRadius = 1;
export const ostGridDotColor = palette.subtle;
export const ostGridDotOpacity = 0.22;

/**
 * The Plan page's Timeline canvas dot grid — same color/opacity/background as the OST canvas's
 * dot grid above, but finer: 3x tighter spacing with a proportionally smaller dot, since the
 * Timeline's own grid geometry (minor cells 8 canvas units — TIMELINE_MINOR_SPACING) is finer
 * than the OST canvas's, so a dot grid at the OST canvas's own spacing read as too coarse here.
 * Derived from ostGridDotSpacing/ostGridDotRadius (not independently chosen) so both stay in
 * sync if the OST canvas's own dot grid ever changes.
 */
export const planCanvasGridDotSpacing = ostGridDotSpacing / 3;
export const planCanvasGridDotRadius = ostGridDotRadius * 0.5;

/**
 * Visual themes for the Plan page's Timeline grid (see TimelineCanvas.tsx, which specializes
 * the generic GridCanvas.tsx engine for that page). Each theme bundles the background, grid
 * pattern (cross-hatch lines vs. a dot per cell), and grid color/size that read as one coherent
 * look — swapping `activeTimelineCanvasTheme` below is the single point that changes the whole
 * canvas's appearance, so a later user-facing toggle only needs to set that one value.
 */
export type TimelineCanvasThemeName = 'blueprint' | 'lightDots';

export type TimelineCanvasTheme = {
    /** Canvas background fill. */
    background: string;
    /** Grid pattern rendered on top of the background. */
    gridStyle: 'lines' | 'dots';
    /** Color of the grid pattern. */
    gridColor: string;
    /** Opacity of both the major and minor grid layers. */
    gridOpacity: number;
    /** Dot radius/spacing (canvas units) — only meaningful when gridStyle='dots'. */
    gridDotRadius?: number;
    gridDotSpacing?: number;
    /** Color/width of the line separating the frozen row/column from the scrollable canvas body. */
    dividerColor: string;
    dividerWidth: number;
    /** Background of each frozen row/column axis-label cell (PlanAxisLabelCell.tsx's default —
     * overridden per-cell only by an older already-persisted label.color). */
    frozenCellBackground: string;
};

export const timelineCanvasThemes: Record<TimelineCanvasThemeName, TimelineCanvasTheme> = {
    /** Original "blueprint paper" look — blue background, white cross-hatch grid lines, white
     * frozen-pane dividers, and a faint white wash on the frozen row/column's label cells. */
    blueprint: {
        background: '#5f84b8',
        gridStyle: 'lines',
        gridColor: '#ffffff',
        gridOpacity: 0.18,
        dividerColor: '#ffffff',
        dividerWidth: 2,
        frozenCellBackground: 'rgba(255, 255, 255, 0.08)',
    },
    /** The OST canvas's own dot-grid look — same background/color/opacity — but at the Timeline
     * canvas's own finer spacing/dot size (see planCanvasGridDot* above). The frozen row/column
     * reuses the app's own panel chrome instead of its own colors: `dividerColor`/`dividerWidth`
     * match `.plan-timeline-panel`'s own outer border (`var(--border)`, 1px — see app.css) so the
     * frozen/body boundary reads as the same line, and `frozenCellBackground` matches
     * `.page-panel-title`'s own `--panel-title-bg` (app.css) — the same fill every other panel's
     * header uses — rather than a separately chosen shade. (This also fixes the frozen label
     * text's contrast: `.plan-axis-label-cell__input` is hardcoded white, which reads poorly
     * against a light background but cleanly against this darker header fill.) */
    lightDots: {
        background: palette.background,
        gridStyle: 'dots',
        gridColor: ostGridDotColor,
        gridOpacity: ostGridDotOpacity,
        gridDotRadius: planCanvasGridDotRadius,
        gridDotSpacing: planCanvasGridDotSpacing,
        dividerColor: 'var(--border)',
        dividerWidth: 1,
        frozenCellBackground: 'var(--panel-title-bg)',
    },
};

/** Which TimelineCanvasTheme is currently active. Change this single value to switch every Plan
 * page canvas's appearance at once. */
export const activeTimelineCanvasTheme: TimelineCanvasThemeName = 'lightDots';
